import { fetchWithToken } from "../../functions/fetch";
import BlackBg from "../common/PopUp/BlackBg";
import PopUpBody from "../common/PopUp/PopUpBody";
import PopUpContainer from "../common/PopUp/PopUpContainer";
import PopUpHeader from "../common/PopUp/PopUpHeader";
import { MultiSelect } from "react-multi-select-component";
import ButtonGroup from "../common/PopUp/ButtonGroup";

import { useEffect, useState } from "react";
import uniqid from "uniqid";
import { useUpdateMessage } from "../../contexts/updateMessageContext";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { postMessageSchema } from "../../validation/messageValidation";

const MessagePopUp = ({ setShowPopUp }) => {
  const itemName = "訊息";
  const receiverTypeOptions = [
    {
      label: "所有人",
      value: "ALL",
    },
    {
      label: "所有住戶",
      value: "ALL_RENTERS",
    },
    {
      label: "所有非住戶",
      value: "ALL_NON_RENTERS",
    },
    {
      label: "個人（住戶）",
      value: "INDIVIDUAL_RENTERS",
    },
    {
      label: "個人（非住戶）",
      value: "INDIVIDUAL_NON_RENTERS",
    },
  ];

  const chineseStr = {
    allItemsAreSelected: "已選擇所有選項",
    clearSearch: "清除搜尋",
    clearSelected: "清除已選擇選項",
    noOptions: "無選項",
    search: "搜尋",
    selectAll: "選擇全部",
    selectAllFiltered: "選擇全部（已過濾）",
    selectSomeItems: "選擇...",
    create: "新增",
  };

  const [text, setText] = useState("");
  const [receiverType, setReceiverType] = useState(
    receiverTypeOptions[0].value,
  );
  const [showMultiSelect, setShowMultiSelect] = useState(false);
  const [receiverOptions, setReceiverOptions] = useState([]);

  const [usersOption, setUsersOption] = useState([]);
  const [rentersOption, setRentersOption] = useState([]);
  const [nonRentersOption, setNonRentersOption] = useState([]);
  const [receivers, setReceivers] = useState([]);

  const [templateMessageId, setTemplateMessageId] = useState("");
  const [updateTemplateMessage, setUpdateTemplateMessage] = useState(false);
  const [createTemplateMessage, setCreateTemplateMessage] = useState(false);
  const [newTemplateMessageTitle, SetnewTemplateMessageTitle] = useState("");

  // state for ButtonGroup
  const [errors, setErrors] = useState([]);

  const { setUpdateMessage } = useUpdateMessage();
  const queryClient = useQueryClient();

  const fetchOptions = async () => {
    const res = await fetchWithToken(
      "GET",
      `${process.env.REACT_APP_BACKEND_HOST}/api/user`,
    );
    // prevent sending message to user without line id
    const renters = res.users.filter((user) => user.isRenter && user.hasLineId);
    const nonRenters = res.users.filter(
      (user) => !user.isRenter && user.hasLineId,
    );

    const fetchedUsersOptions = res.users.map((user) => {
      return {
        label: `${user.name}, ${user.roomNumber ?? user.memberNumber}`,
        value: user._id,
      };
    });
    setUsersOption(fetchedUsersOptions);
    setReceivers(fetchedUsersOptions); // default option

    setRentersOption(
      renters.map((user) => {
        return {
          label: `${user.name} ${user.roomNumber ? `, 房號：${user.roomNumber}` : "無房號"}`,
          value: user._id,
        };
      }),
    );

    setNonRentersOption(
      nonRenters.map((user) => {
        return {
          label: `${user.name}, 會員編號：${user.memberNumber}`,
          value: user._id,
        };
      }),
    );
  };

  const { data: templateMessages } = useQuery({
    queryKey: ["templateMessages"],
    queryFn: async () =>
      (
        await fetchWithToken(
          "GET",
          `${process.env.REACT_APP_BACKEND_HOST}/api/template-message`,
        )
      ).templateMessages,
    initialData: [],
  });

  useEffect(() => {
    fetchOptions();
  }, []);

  const onChangeText = (event) => {
    setText(event.target.value);
  };

  const onChangeReceiverType = (event) => {
    const value = event.target.value;
    // receiver type
    setReceiverType(value);

    if (value === "INDIVIDUAL_NON_RENTERS" || value === "INDIVIDUAL_RENTERS") {
      setShowMultiSelect(true);
    } else {
      setShowMultiSelect(false);
    }

    // receivers/receiver options
    switch (value) {
      case "ALL":
        setReceivers(usersOption);
        break;
      case "ALL_RENTERS":
        setReceivers(rentersOption);
        break;
      case "ALL_NON_RENTERS":
        setReceivers(nonRentersOption);
        break;
      case "INDIVIDUAL_RENTERS":
        setReceivers([]);
        setReceiverOptions(rentersOption);
        break;
      case "INDIVIDUAL_NON_RENTERS":
        setReceivers([]);
        setReceiverOptions(nonRentersOption);
        break;
      default:
        break;
    }
  };

  const onChangeTemplateMessage = (e) => {
    const { value } = e.target;
    setTemplateMessageId(value);
    const tm = templateMessages.find((tm) => tm._id === value);
    if (tm === undefined) {
      return;
    }
    setText(tm.text);
  };

  const createMessage = async () => {
    const body = {
      receiverType,
      receiverId: receivers.map((option) => option.value),
      text,
    };
    // validate with yup
    try {
      const values = {
        ...body,
        createTemplateMessage,
        updateTemplateMessage,
        newTemplateMessageTitle,
        templateMessageId,
      };
      await postMessageSchema.validate(values);
    } catch (err) {
      setErrors(
        err.errors.map((text) => {
          return { msg: text };
        }),
      );
      return;
    }

    const res = await fetchWithToken(
      "POST",
      `${process.env.REACT_APP_BACKEND_HOST}/api/message`,
      body,
    );

    // create/update template message
    if (createTemplateMessage) {
      const postTmRes = await fetchWithToken(
        "POST",
        `${process.env.REACT_APP_BACKEND_HOST}/api/template-message`,
        {
          title: newTemplateMessageTitle,
          text,
        },
      );

      if (postTmRes.status !== 200) {
        console.error(postTmRes);
      }
    }

    if (updateTemplateMessage) {
      // empty string
      const tm = templateMessages.find((tm) => tm._id === templateMessageId);
      const putTmRes = await fetchWithToken(
        "PUT",
        `${process.env.REACT_APP_BACKEND_HOST}/api/template-message/${templateMessageId}`,
        {
          title: tm.title,
          text,
        },
      );
      if (putTmRes.status !== 200) {
        console.error(putTmRes);
      }
    }

    if (res.status === 200) {
      if (createTemplateMessage || updateTemplateMessage) {
        queryClient.invalidateQueries({ queryKey: ["templateMessages"] });
      }
      setUpdateMessage(true);
      setShowPopUp(false);
    } else {
      setErrors(res.errors);
    }
  };

  const cancel = () => {
    setShowPopUp(false);
  };

  return (
    <>
      <BlackBg cancel={cancel} />
      <PopUpContainer>
        <PopUpHeader itemName={itemName} />
        <PopUpBody>
          <label htmlFor="text" className="justify-self-end">
            發送對象
          </label>
          <select
            className="bg-input-gray p-2 rounded"
            value={receiverType}
            onChange={onChangeReceiverType}
          >
            {receiverTypeOptions.map((option) => (
              <option key={uniqid()} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          {showMultiSelect ? (
            <MultiSelect
              className="col-span-2 w-[20vw] min-w-full"
              options={receiverOptions}
              value={receivers}
              onChange={setReceivers}
              labelledBy="Select"
              overrideStrings={chineseStr}
            />
          ) : (
            <div className="w-[20vw] col-span-2" />
          )}

          <label htmlFor="text" className="justify-self-end">
            公版訊息
          </label>
          <select
            className="bg-input-gray p-2 rounded"
            value={templateMessageId}
            onChange={onChangeTemplateMessage}
          >
            <option value="">無</option>
            {templateMessages.map((tm) => (
              <option key={uniqid()} value={tm._id}>
                {tm.title}
              </option>
            ))}
          </select>
          {/* empty placeholder */}
          <div className="invisible col-span-2" />

          <label htmlFor="text" className="justify-self-end">
            發送內容
          </label>
          <textarea
            id="text"
            value={text}
            onChange={onChangeText}
            className="bg-input-gray pl-1 col-span-3"
          />

          <div className="col-span-4 flex justify-between items-center">
            <div className="flex gap-1">
              <input
                type="checkbox"
                id="update-tm"
                checked={updateTemplateMessage}
                onChange={(e) => setUpdateTemplateMessage(e.target.checked)}
              />
              <label className="mr-4" htmlFor="update-tm">
                儲存此版修改
              </label>
              <input
                type="checkbox"
                id="create-tm"
                checked={createTemplateMessage}
                onChange={(e) => setCreateTemplateMessage(e.target.checked)}
              />
              <label htmlFor="create-tm">新增公版訊息</label>
            </div>
            <div className={`${createTemplateMessage ? "" : "invisible"}`}>
              <label htmlFor="new-tm-title" className="mr-2">
                新增公版標題
              </label>
              <input
                id="new-tm-title"
                className="bg-input-gray rounded pl-2"
                value={newTemplateMessageTitle}
                onChange={(e) => SetnewTemplateMessageTitle(e.target.value)}
              />
            </div>
          </div>

          <ButtonGroup
            errors={errors}
            canDelete={false}
            cancelFunction={cancel}
            updateFunction={createMessage}
          />
        </PopUpBody>
      </PopUpContainer>
    </>
  );
};

export default MessagePopUp;
